import { Object3D, Intersection, Group, Matrix4, Vector3, Raycaster, Scene, Euler, MathUtils, Object3DEventMap } from "three";
import { IWebXRState, IXRInteractionType, useWebXRStore } from "../../../zustand/webxr-store";
import { Camera } from "@react-three/fiber";
import { IComponentsById, IPlaySoundAction, ITuple3, IVector3 } from "@r3f-component-data-structure";
import { PresetAnimationController } from "@r3f/animationControllers/presetAnimationController";
import { getAllAnimationControllerOptions } from "../../../utils/action";
import { IEntityActionTypes, ISceneActions, ITransCatTypes, useActionStateStore, useSceneStore } from "../../../zustand";
import { getAllOnloadModelAnimationPlayDataBySceneId } from "@r3f/utils/actions";
import { isAbstractComponent } from "@r3f/utils/general";

export const transformCursorByIntersection = (cursor: Object3D, intersection: Intersection<Object3D<Object3DEventMap>>) => {
    if (!intersection.face) return {};
    const localPoint = intersection.object.worldToLocal(intersection.point.clone())
    localPoint.add(intersection.face.normal.clone());
    const worldPoint = intersection.object.localToWorld(localPoint);
    cursor.position.set(intersection.point.x, intersection.point.y, intersection.point.z)
    cursor.lookAt(worldPoint);
}

export const sortIntersectionsByRenderOrder = (a: Intersection<Object3D<Object3DEventMap>>, b: Intersection<Object3D<Object3DEventMap>>) => {
    if ( a.object.renderOrder < b.object.renderOrder ){
        return -1;
    }
    if ( a.object.renderOrder > b.object.renderOrder ){
        return 1;
    }
    return 0;
}

export const intialiseWebxrSession = async (options: XRSessionInit = {}): Promise<null | XRSession> => {
    // Check if webxr is supported
    if (typeof navigator.xr === 'undefined') return null;
    
    // Create overlay with x to allow user to exit webxr session
    const overlay = document.createElement('div');
    overlay.style.display = 'none';
    document.body.appendChild( overlay );
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '38px');
    svg.setAttribute('height', '38px');
    svg.style.position = 'absolute';
    svg.style.right = '20px';
    svg.style.top = '20px';
    svg.addEventListener('click', () =>  session?.end());
    overlay.appendChild( svg );
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M 12,12 L 28,28 M 28,12 12,28');
    path.setAttribute('stroke', '#fff');
    path.setAttribute('stroke-width', '2px');
    svg.appendChild( path );

    if (options.optionalFeatures === undefined) options.optionalFeatures = [];
    options.optionalFeatures.push( 'dom-overlay' );
    options.domOverlay = { root: overlay };

    let session :null | XRSession  = null;
    try {
        const arSupported = !!(await navigator.xr?.isSessionSupported('immersive-ar'))
        session = await navigator?.xr?.requestSession('immersive-ar', {optionalFeatures: ['local-floor', 'bounded-floor', 'hit-test']})
    } catch (error) {
        console.log('error setting up session: ', error)
        session = null;
    }
    
    return session;
}

interface IBaseSelectCbArgs {
    raycaster: Raycaster;    
    controllerGroup: Group;
    sceneId: string;
}

interface IOnSelectCbArgs extends IBaseSelectCbArgs {
    selectHasRunRef: React.RefObject<boolean>; 
}

interface IBaseSelectChangeCbArgs extends IBaseSelectCbArgs {
    handedness: XRHandedness; 
    activeSelectHandRef: React.RefObject<XRHandedness[]>; 
    setActiveSelectHand: (value: XRHandedness[]) => void; 
}

interface IOnSelectStartCbArgs extends IBaseSelectChangeCbArgs {
    selectStartHasRunRef: React.RefObject<boolean>; 
}

interface IOnSelectEndCbArgs extends IBaseSelectChangeCbArgs {
    selectEndHasRunRef: React.RefObject<boolean>; 
}

export const onSelectStartCb = ({raycaster, handedness, selectStartHasRunRef, activeSelectHandRef, setActiveSelectHand, controllerGroup, sceneId}: IOnSelectStartCbArgs) => {
    // if (selectStartHasRunRef) {
        if (!activeSelectHandRef.current.includes(handedness)) {
            setActiveSelectHand([...activeSelectHandRef.current, handedness])
        }
        const store = useWebXRStore.getState();
        const intersections = getControllerRayIntersections(Array.from(store.interactions.keys()), raycaster, controllerGroup, sceneId);
        callIntersectionObjectEventHandlers(store, intersections, 'onSelectStart');
    // }
    selectStartHasRunRef.current = true;
}

export const onSelectEndCb = ({raycaster, selectEndHasRunRef, activeSelectHandRef, handedness, setActiveSelectHand, controllerGroup, sceneId}: IOnSelectEndCbArgs) => {
    // if (selectEndHasRunRef) {
        if (activeSelectHandRef.current.includes(handedness)) {
            setActiveSelectHand(activeSelectHandRef.current.filter(_handedness => _handedness !== handedness))
        }
        const store = useWebXRStore.getState();
        const intersections = getControllerRayIntersections(Array.from(store.interactions.keys()), raycaster, controllerGroup, sceneId);
        callIntersectionObjectEventHandlers(store, intersections, 'onSelectEnd');
    // }
    selectEndHasRunRef.current = true;
}

export const onSelectCb = ({raycaster, selectHasRunRef, controllerGroup, sceneId}: IOnSelectCbArgs) => {
    // if (selectHasRunRef.current) {
        const store = useWebXRStore.getState();
        const intersections = getControllerRayIntersections(Array.from(store.interactions.keys()), raycaster, controllerGroup, sceneId);
        callIntersectionObjectEventHandlers(store, intersections, 'onSelect');
    // };
    selectHasRunRef.current = true;
}

export type IHitData = {detected: true, xrPose: XRPose} | {detected: false};

export const getHitData = (
    xrFrame: XRFrame, 
    xrHitTestSource: XRHitTestSource, 
    localReferenceSpace: XRReferenceSpace | XRBoundedReferenceSpace | undefined
): IHitData => {
   // if (!session || !hitTestEnabled || !xrHitTestSource ) return; 
    const hitTestResults = xrFrame.getHitTestResults(xrHitTestSource);
    if (hitTestResults.length) {
        if (localReferenceSpace) {
            const xrPose = hitTestResults[0].getPose(localReferenceSpace);
            if (xrPose) return {detected: true, xrPose}
        } 
    } 
    return {detected: false};
    //this._session.requestAnimationFrame(getHitDetected);
}


export const setHitTestSource = async (xrHitTestSource: React.RefObject<XRHitTestSource | undefined>, xrInputSource: XRInputSource | null, session: XRSession | undefined) => {
    let hitTestSpace = xrInputSource?.targetRaySpace ?? await session?.requestReferenceSpace('viewer');
    // console.log('hitTestSpace', xrInputSource)
    if (!hitTestSpace) return;
    xrHitTestSource.current = await session?.requestHitTestSource?.({space: hitTestSpace})
    //console.log('xrHitTestSource', xrHitTestSource)
};

export const setLocalReferenceSpace = async (localReferenceSpace: React.RefObject<XRReferenceSpace | XRBoundedReferenceSpace | undefined>, session: XRSession | undefined) => {
    localReferenceSpace.current = await session?.requestReferenceSpace('local');
}

const tempHitMatrix = new Matrix4();
const tempTarget = new Vector3();

export const transformPlacementGroup = (
    hit: IHitMatrixData, 
    placementGroup: React.RefObject<Group | undefined>, 
    camera: Camera, 
    rotationMatrixRef: React.RefObject<Matrix4>,
    tempScaleMatrixRef: React.RefObject<Matrix4>
) => {
    if (!hit.detected || !placementGroup) return;

    const hitMatrix = hit.hitMatrixArray ? tempHitMatrix.fromArray(hit.hitMatrixArray) : hit.hitMatrix!;

    tempTarget
        .setFromMatrixPosition(hitMatrix)
        .setComponent(1, camera.position.y);

    placementGroup.current?.matrix
        .copy(hitMatrix)
        .lookAt(camera.position, tempTarget, placementGroup.current.up)
        .multiply(rotationMatrixRef.current)
        .multiply(tempScaleMatrixRef.current)
}

const tempMatrix = new Matrix4();

export const getControllerRayIntersections = (objects: Object3D<Object3DEventMap>[], raycaster: Raycaster, controllerGroup: Group, sceneId?: string): Intersection<Object3D<Object3DEventMap>>[] | undefined => {
    const _objects = !!sceneId ? objects.filter(obj => obj.name === `sceneId_${sceneId}`) : objects;
    if (_objects.length === 0 || !controllerGroup) return;
    tempMatrix.identity().extractRotation(controllerGroup.matrixWorld)
    raycaster.firstHitOnly = true;
    raycaster.ray.origin.setFromMatrixPosition(controllerGroup.matrixWorld)
    raycaster.ray.direction.set(0, 0, -1).applyMatrix4(tempMatrix)
    return raycaster.intersectObjects(_objects, true);
}

// below loop based on https://github.com/pmndrs/react-xr/blob/master/src/Interactions.tsx#L76
// interaction handlers are stored in a global zustand store and called here with the intersection data
//let interSectionObject: Object3D | null = null;
const callIntersectionObjectEventHandlers = (store: IWebXRState, intersections: Intersection<Object3D<Object3DEventMap>>[] | undefined, eventType: IXRInteractionType): Object3D | null => {
    if (!intersections) return null;
    const { interactions } = useWebXRStore.getState();

    let eventObjectWithHandler: Object3D | null = null;

    for (const intersection of intersections) {
        let eventObject: Object3D | null = intersection.object;

        while (eventObject) {
            if (hasInteraction(store, eventObject, eventType)) {
                eventObjectWithHandler = eventObject;
                const interaction = interactions.get(eventObject)
                // console.log(interaction)
                if (interaction) {
                    interaction[eventType].current?.({ intersection, intersections })  
                }
            }
            eventObject = eventObject.parent
        }
    }
    return eventObjectWithHandler;
}

const hasInteraction = (store: IWebXRState, object: Object3D, eventType: IXRInteractionType): boolean => {
    return [store.interactions.get(object)?.[eventType]].some((handlerRef) => handlerRef?.current)
}

export const sleep = (delay: number) => new Promise((resolve) => setTimeout(resolve, delay))


export const enterSceneById = (sceneId: string, componentsById: IComponentsById |null, presetAnimationController: PresetAnimationController, sceneOnLoadPlaySoundActions: IPlaySoundAction[]) => {
    if (!componentsById || !sceneId) return;
    presetAnimationController
        .set({options: getAllAnimationControllerOptions(componentsById).preset})
        .startAllOnloadAnimationsBySceneId(sceneId)

    useActionStateStore.dispatch({
        type: IEntityActionTypes.PLAY_MODEL_ANIMATIONS_BY_ID,
        payload: getAllOnloadModelAnimationPlayDataBySceneId(componentsById, sceneId)
    })              

    useSceneStore.dispatch({
        type: ISceneActions.SET_TRANSITION_CATEGORY,
        transCatDict: {
            [sceneId]: ITransCatTypes.enter,
        },
    });

    sceneOnLoadPlaySoundActions?.forEach((action) => {
        let { stopMedia, loop } = action;
        if (stopMedia) {
            useActionStateStore.dispatch({
                type: IEntityActionTypes.PAUSE_ALL_VIDEOS,
            });
            useActionStateStore.dispatch({
                type: IEntityActionTypes.PAUSE_ALL_SCENE_AUDIO_BY_SCENE_ID,
                payload: { id: sceneId },
            });
            useActionStateStore.dispatch({
                type: IEntityActionTypes.PAUSE_BACKGROUND_AUDIO,
            });
        }
        useActionStateStore.dispatch({
            type: IEntityActionTypes.PLAY_ALL_SCENE_AUDIO_BY_SCENE_AND_ENTITY_ID,
            payload: { sceneId, entityIds: [sceneId] },
        });
    })
}

export const resetSceneById = (sceneId: string, componentsById: IComponentsById | null, presetAnimationController: PresetAnimationController, threeScene: Scene) => {
    presetAnimationController.resetAllAnimationsBySceneId(sceneId);

    useActionStateStore.dispatch({ type: IEntityActionTypes.PAUSE_ALL_MODEL_ANIMATIONS })

    if (componentsById) {
        // Show any entities that were hidden by a toggleVisibility action
        Object.entries(componentsById).forEach(([id, component]) => {
            if(isAbstractComponent(component)) return;
            const prospectiveGroupId = `toggleVisibilityGroup-${id}`;
            const threeGroup = threeScene.getObjectByName(prospectiveGroupId);
            if (threeGroup) threeGroup.visible = !component.isHidden;
        });
    }
}

const hitMatrix = new Matrix4();

export type IHitMatrixData = {
    detected: true;
    hitMatrixArray?: Float32Array; 
    hitMatrix?: Matrix4;
} | {detected: false}

export const getGroundPlaneHitData = (groundPlane: Object3D<Object3DEventMap>, raycaster: Raycaster, controllerGroup: Group): IHitMatrixData => {
    const intersections = getControllerRayIntersections([groundPlane], raycaster, controllerGroup)
    if (!intersections?.length) return { detected: false };

    return { detected: true, hitMatrix: hitMatrix.setPosition(intersections[0].point)}
}

export const getInvertEulerRotationFromDegreeTuple = (rotation: ITuple3| IVector3): Euler => {
    const e0 = new Euler(
        MathUtils.degToRad(rotation[0]), 
        MathUtils.degToRad(rotation[1]), 
        MathUtils.degToRad(rotation[2])
    )

    const rotationMatrix = new Matrix4().makeRotationFromEuler(e0).invert();
    return new Euler().setFromRotationMatrix(rotationMatrix);
}
